import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_ENDPOINTS, DEFAULT_CID } from '../config/api.config';
import { ApiResponse, LoginRequest, RegisterRequest, UserCredentials } from '../models/auth.model';
import { environment } from '../../../environments/environment';
import { CartStoreService } from './cart-store.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  readonly currentUser = signal<UserCredentials | null>(this.getStoredUser());
  readonly isAuthenticated = signal<boolean>(!!this.getStoredUser());

  constructor(
    private http: HttpClient,
    private cartStore: CartStoreService
  ) { }


  /**
   * Ensures a valid JWT token is available for catalog and API calls.
   * If not logged in, authenticates the public session with 9090909090.
   */
  ensureToken(): Observable<string> {
    if (typeof window === 'undefined') return of('');
    const existing = sessionStorage.getItem('token');
    if (existing) {
      return of(existing);
    }

    const payload: LoginRequest = {
      phone: "",
      password: "",
      cid: DEFAULT_CID
    };

    return this.http.post<ApiResponse<UserCredentials>>(API_ENDPOINTS.LOGIN, payload).pipe(
      map(res => {
        const token = res?.response?.token || '';
        if (token && typeof window !== 'undefined') {
          sessionStorage.setItem('token', token);
        }
        return token;
      }),
      catchError(err => {
        console.warn('ensureToken notice:', err);
        return of('');
      })
    );
  }


  private getStoredUser(): UserCredentials | null {
    if (typeof window === 'undefined') return null;
    try {
      const data = sessionStorage.getItem('userCreds');
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  login(credentials: { phone: string; password: string }): Observable<ApiResponse<UserCredentials>> {
    const phone = String(credentials.phone || '').trim();
    const password = String(credentials.password || '');

    const payload: LoginRequest = {
      phone: phone,
      password: password,
      cid: DEFAULT_CID
    };

    const applyUserSession = (user: UserCredentials) => {
      sessionStorage.setItem('userCreds', JSON.stringify(user));
      if (user.token) {
        sessionStorage.setItem('token', user.token);
      }
      if (user.cid) {
        sessionStorage.setItem('cid', String(user.cid));
      }
      if (user.userId) {
        sessionStorage.setItem('userId', String(user.userId));
      }
      if (user.userName) {
        sessionStorage.setItem('userName', user.userName);
      }
      if (user.userMobileNo) {
        sessionStorage.setItem('userMobileNo', user.userMobileNo);
      }
      if (user.userEmail) {
        sessionStorage.setItem('userEmail', user.userEmail);
      }
      this.currentUser.set(user);
      this.isAuthenticated.set(true);
      if (user.userId) {
        this.cartStore.loadUserStore(user.userId);
      }
    };

    return this.http.post<ApiResponse<UserCredentials>>(API_ENDPOINTS.LOGIN, payload).pipe(
      map(response => {
        if (response && response.response && (response.response.userId || response.response.token || response.response.userName)) {
          applyUserSession(response.response);
          return response;
        }
        throw new Error(response?.message || 'Invalid username or password');
      })
    );
  }

  register(data: Partial<RegisterRequest>): Observable<ApiResponse<any>> {
    const body: any = {
      version: 0,
      isActive: true,
      description: 'Customer Account',
      name: String(data.name || '').trim(),
      geoAddress: [],
      mobileNo1: String(data.mobileNo1 || '').trim(),
      mobileNo2: '',
      emailId: String(data.emailId || '').trim(),
      password: String(data.password || ''),
      customerParamEntityList: [],
      status: [
        {
          customerRegistrationStatus: 'REQUESTED',
          updatedDate: Date.now()
        }
      ],
      latestStatus: 'REQUESTED',
      image: '',
      documents: [],
      cid: DEFAULT_CID
    };

    return this.http.post<ApiResponse<any>>(API_ENDPOINTS.REGISTER, body);
  }

  logout() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('userinputCreds');
      sessionStorage.clear();
    }
    this.currentUser.set(null);
    this.isAuthenticated.set(false);
    this.cartStore.clearSession();
    // Re-acquire public browsing token for guest catalog experience
    this.ensureToken().subscribe();
  }



  isLoggedIn(): boolean {
    return this.isAuthenticated() || !!this.currentUser();
  }
}
